import { isAxiosError } from 'axios';

import { HttpStatusCode } from '../../enums/index.ts';

export type ServerFormValidationError = {
  message: string;
  field: string;
};

export type ServerErrorData = Record<string, string>;

/**
 * Extracts error message from various Axios error types
 * https://axios-http.com/ru/docs/handling_errors
 *
 * @param {unknown} e - Caught Error
 * @returns {string} - main error
 */
export function getErrorMessage(e: unknown): string {
  if (isAxiosError(e)) {
    // The request was made and the server responded with a status code that is beyond 2xx
    if (e.response) {
      if (e.response.status === HttpStatusCode.UNAUTHORIZED_401) {
        return '';
      }

      const serverResponse = e.response.data as ServerErrorData;
      const serverMessage = serverResponse?.root || serverResponse?.message;

      return Array.isArray(serverMessage)
        ? e.response.statusText
        : serverMessage || e.response.statusText || e.message;
    }
    // No response received (XMLHttpRequest)
    if (e.request) {
      return `Network error: ${JSON.stringify({
        url: e.config?.url,
        method: e.config?.method,
        timestamp: new Date().toISOString(),
      })}`;
    }
    // An error occurred BEFORE the request was sent.
    return e.message;
  }

  // Any other unexpected error
  return JSON.stringify(e);
}

export function getErrorsObj(e: unknown): ServerErrorData {
  if (isAxiosError(e)) {
    // The request was made and the server responded with a status code that is beyond 2xx
    if (e.response) {
      const serverResponse = e.response.data as ServerErrorData;
      const output = {} as ServerErrorData;
      for (const key in serverResponse) {
        if (key !== 'root') {
          output[key] = serverResponse[key];
        }
      }
      return output;
    }
    // No response received (XMLHttpRequest)
    if (e.request) {
      return {
        root: `Network error: ${JSON.stringify({
          url: e.config?.url,
          method: e.config?.method,
          timestamp: new Date().toISOString(),
        })}`,
      };
    }
    // An error occurred BEFORE the request was sent.
    return {
      root: e.message,
    };
  }

  // Any other unexpected error
  return {
    root: JSON.stringify(e),
  };
}

export function getErrorsObjFormAuth(e: unknown): ServerErrorData {
  if (isAxiosError(e)) {
    if (e.response && e.response.status === HttpStatusCode.BAD_REQUEST_400) {
      const serverResponse = e.response.data.message as string[];
      const output = {} as ServerErrorData;
      serverResponse.forEach((field) => {
        const name = field.slice(0, field.indexOf(' '));
        if (!output[name]) {
          output[name] = field.slice(field.indexOf(name) + name.length + 1, field.length);
        }
      });
      return output;
    }
    // No response received (XMLHttpRequest)
    if (e.request) {
      return {
        root: `Network error: ${JSON.stringify({
          url: e.config?.url,
          method: e.config?.method,
          timestamp: new Date().toISOString(),
        })}`,
      };
    }

    // An error occurred BEFORE the request was sent.
    return {
      root: e.message,
    };
  }
  // Any other unexpected error
  return {
    root: JSON.stringify(e),
  };
}
